import { LitElement, html } from "lit";
import { renderQuestion } from "./quiz/quiz-question";

// CSRF helper for same-origin POST requests
const csrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ||
    "";
const jsonHeaders = () => ({
  "Content-Type": "application/json",
  "X-CSRF-Token": csrfToken(),
});

class LearnApp extends LitElement {
  static properties = {
    state: { type: String }, // "sections" | "running" | "feedback"
    sections: { type: Array },
    currentSection: { type: Object },
    currentQuestion: { type: Object },
    lastAnswered: { type: Object },
    isLastInSection: { type: Boolean },
    loading: { type: Boolean },
    error: { type: String },
    authenticated: {
      type: Boolean,
      attribute: "data-authenticated",
      reflect: false,
    },
    loginUrl: { type: String, attribute: "data-login-url", reflect: false },
    sectionOnlyMissing: { type: Object },
    currentOnlyMissing: { type: Boolean },
  };

  constructor() {
    super();
    this.state = "sections";
    this.sections = [];
    this.currentSection = null;
    this.currentQuestion = null;
    this.lastAnswered = null;
    this.isLastInSection = false;
    this.loading = false;
    this.error = null;

    // auth mode; default to false until we learn otherwise
    this.authenticated = false;
    this.loginUrl = "/zaloguj";
    this.sectionOnlyMissing = {};
    this.currentOnlyMissing = false;

    // następne pytanie po feedbacku (jeśli nie jest ostatnie w sekcji)
    this._nextQuestion = null;

    // handler klawiatury
    this._onKeydown = this._onKeydown.bind(this);
  }

  createRenderRoot() {
    // Render w light DOM, żeby działał Tailwind
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    // Initialize auth flags from attributes (we avoid relying on Boolean attribute semantics)
    const attrAuth = this.getAttribute("data-authenticated");
    if (attrAuth !== null) {
      this.authenticated = String(attrAuth).toLowerCase() === "true";
    }
    const attrLogin = this.getAttribute("data-login-url");
    if (attrLogin) this.loginUrl = attrLogin;

    window.addEventListener("keydown", this._onKeydown);
    this.loadSections();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeydown);
  }

  /* ------------------------------------------------------------------
   * API / stan
   * ------------------------------------------------------------------ */

  async loadSections() {
    this.loading = true;
    this.error = null;
    try {
      const resp = await fetch("/api/learning/sections");
      if (!resp.ok) throw new Error("Nie udało się pobrać sekcji");
      const data = await resp.json();
      if (typeof data.authenticated !== "undefined") {
        this.authenticated = !!data.authenticated;
      }
      this.sections = data.sections || [];
      this.syncSectionOnlyMissingState();
      this.updateGlobalProgressRing();
      this.state = "sections";
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  async startSection(section) {
    if (!this.authenticated) {
      // Guest mode: cannot start learning, present info via banner; no-op
      return;
    }
    const sectionId = section?.id;
    if (!sectionId) return;
    const onlyMissing = this.isSectionOnlyMissingEnabled(section)
      ? !!this.sectionOnlyMissing[String(sectionId)]
      : false;

    this.loading = true;
    this.error = null;
    try {
      const resp = await fetch(`/api/learning/sections/${sectionId}/start`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          only_missing_to_hundred_percent: onlyMissing,
        }),
      });
      if (!resp.ok) throw new Error("Nie udało się rozpocząć sekcji");
      const data = await resp.json();

      if (data.end_of_section) {
        // nic już do zrobienia – wróć do listy
        await this.loadSections();
        return;
      }

      this.currentSection = data.section;
      this.currentQuestion = data.current_question;
      this.lastAnswered = null;
      this.isLastInSection = false;
      this.currentOnlyMissing = !!data.only_missing_to_hundred_percent;
      this._nextQuestion = null;
      this.state = "running";
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  async submitAnswer(selected) {
    if (!this.currentSection || !this.currentQuestion) return;
    this.loading = true;
    this.error = null;

    try {
      const resp = await fetch(
          `/api/learning/sections/${this.currentSection.id}/answer`,
          {
            method: "POST",
            headers: jsonHeaders(),
            body: JSON.stringify({
              question_id: this.currentQuestion.id,
              selected,
              only_missing_to_hundred_percent: this.currentOnlyMissing,
            }),
          },
      );
      if (!resp.ok) throw new Error("Nie udało się zapisać odpowiedzi");
      const data = await resp.json();

      // Zapisz feedback + ewentualne kolejne pytanie
      this.lastAnswered = data.answered;
      this.isLastInSection = !!data.end_of_section;
      this._nextQuestion = data.next_question || null;

      // UWAGA: currentQuestion zostaje pytaniem, na które właśnie odpowiedzieliśmy
      this.state = "feedback";
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  continueAfterFeedback() {
    if (this.isLastInSection) {
      this.finishSection();
      return;
    }

    if (!this._nextQuestion) {
      // awaryjnie – brak next_question, wróć do listy
      this.loadSections();
      return;
    }

    this.currentQuestion = this._nextQuestion;
    this._nextQuestion = null;
    this.lastAnswered = null;
    this.state = "running";
    this.requestUpdate();
  }

  async interrupt() {
    if (!this.currentSection) {
      this.loadSections();
      return;
    }
    try {
      await fetch(`/api/learning/sections/${this.currentSection.id}/interrupt`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({}),
      });
    } catch (_e) {}
    this.currentSection = null;
    this.currentQuestion = null;
    this.lastAnswered = null;
    this.currentOnlyMissing = false;
    this._nextQuestion = null;
    await this.loadSections();
  }

  async finishSection() {
    if (!this.currentSection) return;
    try {
      await fetch(`/api/learning/sections/${this.currentSection.id}/finish`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({}),
      });
    } catch (_e) {}
    this.currentSection = null;
    this.currentQuestion = null;
    this.lastAnswered = null;
    this.currentOnlyMissing = false;
    this._nextQuestion = null;
    await this.loadSections();
  }

  async resetSection(sectionId) {
    if (!this.authenticated) return;

    const confirmed = window.confirm(
      "Na pewno zresetować postęp tej sekcji do 0%?",
    );
    if (!confirmed) return;

    this.loading = true;
    this.error = null;

    try {
      const resp = await fetch(`/api/learning/sections/${sectionId}/reset`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error("Nie udało się zresetować sekcji");
      await this.loadSections();
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  updateGlobalProgressRing() {
    const root = document.querySelector(".global-progress");
    if (!root) return;

    const ring = root.querySelector(".global-progress__ring");
    const value = root.querySelector(".global-progress__value");
    if (!ring || !value) return;

    let percent = 0;

    if (this.authenticated && Array.isArray(this.sections) && this.sections.length > 0) {
      let totalQuestions = 0;
      let mastered = 0;
      let halfMastered = 0;

      this.sections.forEach((section) => {
        totalQuestions += Number(section.questions_count) || 0;
        mastered += Number(section.mastered_count) || 0;
        halfMastered += Number(section.half_mastered_count) || 0;
      });

      if (totalQuestions > 0) {
        percent = ((mastered + 0.5 * halfMastered) / totalQuestions) * 100;
      }
    }

    const clamped = Math.max(0, Math.min(100, percent));
    const rounded = clamped.toFixed(1);
    ring.style.setProperty("--progress", rounded);
    value.textContent = `${rounded}%`;
  }

  syncSectionOnlyMissingState() {
    const nextState = {};
    (this.sections || []).forEach((section) => {
      const key = String(section.id);
      nextState[key] = this.isSectionOnlyMissingEnabled(section)
        ? !!this.sectionOnlyMissing[key]
        : false;
    });
    this.sectionOnlyMissing = nextState;
  }

  isSectionOnlyMissingEnabled(section) {
    const progressPercent = Number(section?.progress_percent) || 0;
    return progressPercent < 100;
  }

  toggleSectionOnlyMissing(sectionId, checked) {
    const key = String(sectionId);
    this.sectionOnlyMissing = {
      ...this.sectionOnlyMissing,
      [key]: !!checked,
    };
  }

  /* ------------------------------------------------------------------
   * Obsługa klawiatury (jak w quiz-app)
   * ------------------------------------------------------------------ */

  _isInteractiveTarget(target) {
    if (!target) return false;
    const tag = target.tagName?.toLowerCase();
    const editable = target.getAttribute?.("contenteditable");

    if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        editable === "" ||
        editable === "true"
    ) {
      return true;
    }

    if (tag === "video") return true;

    return false;
  }

  _onKeydown(event) {
    if (event.altKey || event.metaKey || event.ctrlKey) return;
    if (this._isInteractiveTarget(event.target)) return;

    const key = event.key.toLowerCase();

    // tryb odpowiadania
    if (this.state === "running" && this.currentQuestion) {
      const q = this.currentQuestion;
      const isTF = q.correct === "T" || q.correct === "N";

      if (key === "escape") {
        event.preventDefault();
        this.interrupt();
        return;
      }

      if (isTF) {
        if (key === "t" || key === "1") {
          event.preventDefault();
          this.submitAnswer("T");
          return;
        }
        if (key === "n" || key === "2") {
          event.preventDefault();
          this.submitAnswer("N");
          return;
        }
      } else {
        if (key === "1" || key === "a") {
          event.preventDefault();
          this.submitAnswer("A");
          return;
        }
        if (key === "2" || key === "b") {
          event.preventDefault();
          this.submitAnswer("B");
          return;
        }
        if (key === "3" || key === "c") {
          event.preventDefault();
          this.submitAnswer("C");
          return;
        }
      }

      return;
    }

    // tryb feedback
    if (this.state === "feedback" && this.lastAnswered) {
      if (key === "escape" && !this.isLastInSection) {
        event.preventDefault();
        this.interrupt();
        return;
      }

      if (key === "enter" || key === " ") {
        event.preventDefault();
        if (this.isLastInSection) {
          this.finishSection();
        } else {
          this.continueAfterFeedback();
        }
      }
    }
  }

  /* ------------------------------------------------------------------
   * Render – spójny z quiz-app
   * ------------------------------------------------------------------ */

  render() {
    return html`
      <div class="max-w-4xl mx-auto space-y-4">
        ${this.error ? this._renderError() : null}

        <div class="bg-white border rounded-xl shadow-sm overflow-hidden">
          ${this._renderHeader()}

          <div class="p-5">
            ${this.state === "sections" ? this.renderSections() : null}
            ${this.state === "running" ? this.renderRunning() : null}
            ${this.state === "feedback" ? this.renderFeedback() : null}
          </div>
        </div>
      </div>
    `;
  }

  _renderError() {
    return html`
      <div
          class="mt-2 flex items-center gap-2 rounded-md bg-red-50 text-red-800 border border-red-200 px-4 py-3 text-sm"
      >
        <span>${this.error}</span>
        <button
            class="ml-auto text-red-700 underline"
            @click=${() => (this.error = null)}
        >
          Ukryj
        </button>
      </div>
    `;
  }

  _renderHeader() {
    const section = this.currentSection;
    const inLesson = this.state === "running" || this.state === "feedback";

    return html`
      <div class="px-5 pt-4 pb-4 border-b border-gray-100 bg-gray-50/60">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold tracking-tight text-gray-900">
              Tryb nauki
            </h1>
            <p class="mt-1 text-sm text-gray-500">
              Ucz się sekcjami. Po każdym pytaniu zobaczysz krótką informację
              zwrotną.
            </p>
          </div>

          ${inLesson
              ? html`
                <div class="flex flex-col items-end gap-1">
                  <span
                      class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 whitespace-nowrap"
                  >
                    <span
                        class="h-2 w-2 rounded-full bg-primary-500 animate-pulse"
                    ></span>
                    W trakcie nauki
                  </span>
                  ${section
                      ? html`
                        <span class="text-xs text-gray-600">
                          Sekcja <span class="font-semibold">${section.nr}</span>
                        </span>
                      `
                      : null}
                </div>
              `
              : html`
                <div
                    class="hidden sm:flex flex-col items-end text-right text-xs text-gray-500"
                >
                  <span>Wybierz sekcję, od której chcesz zacząć.</span>
                </div>
              `}
        </div>
      </div>
    `;
  }

  renderSections() {
    if (this.loading && this.sections.length === 0) {
      return html` <div class="text-sm text-gray-500">Ładowanie sekcji…</div> `;
    }

    if (!this.sections.length) {
      return html`
        <div class="text-sm text-gray-500">Brak sekcji do nauki.</div>
      `;
    }

    return html`
      <div class="space-y-4">
        ${!this.authenticated
            ? html`
              <div
                  class="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              >
                <span
                    class="mt-0.5 inline-flex h-2 w-2 rounded-full bg-amber-500"
                ></span>
                <div>
                  Ta funkcja jest dostępna tylko dla zalogowanych użytkowników.
                  <a
                      class="underline font-medium text-amber-900 hover:text-amber-700"
                      href="${this.loginUrl}"
                  >Zaloguj się</a
                  >, aby rozpocząć naukę.
                </div>
              </div>
            `
            : null}
        ${this.sections.map((s) => {
          const hasState = !!s.has_state;
          const masteredCount = s.mastered_count ?? 0;
          const halfMasteredCount = s.half_mastered_count ?? 0;
          const progressPercent = s.progress_percent ?? 0;

          const percentNumber =
              typeof progressPercent === "number"
                  ? progressPercent
                  : Number(progressPercent) || 0;

          const clamped = Math.max(0, Math.min(100, percentNumber));
          // 🔴 -> 🟢 : 0% = czerwony, 100% = zielony
          const hue = Math.round((clamped / 100) * 120); // 0 (red) – 120 (green)
          const ringColor = `hsl(${hue} 72% 45%)`;

          const percentLabel = clamped.toFixed(1);
          const onlyMissingEnabled = this.isSectionOnlyMissingEnabled(s);
          const onlyMissingChecked = !!this.sectionOnlyMissing[String(s.id)];
          const checkboxId = `section-missing-${s.id}`;

          return html`
            <div
                class="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3"
            >
              <!-- lewa część: opis sekcji -->
              <div class="flex-1 space-y-1">
                <div class="text-xs text-gray-500">Sekcja ${s.nr}</div>
                <div class="text-sm font-semibold text-gray-900">
                  ${s.name}
                </div>
                <div class="text-xs text-gray-600">
                  Pytania: ${s.questions_count}
                </div>
                <div class="text-xs text-gray-600">
                  Opanowane: ${masteredCount} • W trakcie (50%):
                  ${halfMasteredCount}
                </div>
                <label
                    for="${checkboxId}"
                    class="mt-2 flex items-center gap-2 text-xs text-gray-700"
                >
                  <input
                      id="${checkboxId}"
                      type="checkbox"
                      class="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
                      .checked=${onlyMissingChecked}
                      ?disabled=${!this.authenticated || !onlyMissingEnabled}
                      @change=${(event) =>
                          this.toggleSectionOnlyMissing(
                              s.id,
                              event.target.checked,
                          )}
                  />
                  <span class="${onlyMissingEnabled ? "" : "text-gray-400"}">
                    Uwzględnij tylko pytania brakujące do osiągnięcia 100%
                  </span>
                </label>
              </div>

              <!-- prawa część: kółko z progresem + przycisk -->
              <div class="flex flex-col items-center gap-2">
                <div class="relative h-16 w-16">
                  <svg class="h-full w-full -rotate-90" viewBox="0 0 64 64" aria-hidden="true">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" stroke-width="4"></circle>
                    <circle
                        cx="32" cy="32" r="28" fill="none" stroke="${ringColor}" stroke-width="4"
                        stroke-linecap="round" pathLength="100" stroke-dasharray="100"
                        stroke-dashoffset="${100 - clamped}"
                    ></circle>
                  </svg>
                  <div
                      class="absolute inset-1 flex items-center justify-center rounded-full bg-white"
                  >
                    <span class="text-xs font-semibold text-gray-700">
                      ${percentLabel}%
                    </span>
                  </div>
                </div>

                <button
                    class="btn btn-primary text-xs px-4 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="${this.authenticated
                        ? ""
                        : "Zaloguj się, aby rozpocząć naukę"}"
                    ?disabled=${!this.authenticated}
                    aria-disabled="${!this.authenticated}"
                    @click=${() => this.startSection(s)}
                >
                  ${hasState ? "Kontynuuj" : "Start"}
                </button>
                <button
                    class="text-xs text-gray-500 underline underline-offset-2 hover:text-red-700 disabled:no-underline disabled:opacity-50 disabled:cursor-not-allowed"
                    title="${this.authenticated
                        ? ""
                        : "Zaloguj się, aby zresetować postęp"}"
                    ?disabled=${!this.authenticated}
                    aria-disabled="${!this.authenticated}"
                    @click=${() => this.resetSection(s.id)}
                >
                  Reset (ustaw 0%)
                </button>
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  renderRunning() {
    const q = this.currentQuestion || null;

    const section = this.currentSection;
    const onSelect = (opt) => this.submitAnswer(opt);

    return html`
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-600">
            Sekcja ${section?.nr}: ${section?.name}
          </div>
          <button class="btn btn-muted" @click=${() => this.interrupt()}>
            Przerwij
          </button>
        </div>

        ${renderQuestion({ q, selected: null, onSelect })}
      </div>
    `;
  }

  renderFeedback() {
    const a = this.lastAnswered;
    const section = this.currentSection;

    const q = this.currentQuestion || null;

    if (!a || !q) {
      return html`
        <div class="text-sm text-gray-500">Brak danych do wyświetlenia.</div>
      `;
    }

    const isTF = a.correct_answer === "T" || a.correct_answer === "N";
    const label = (opt) => {
      if (opt == null) return "";
      if (isTF) return opt === "T" ? "TAK" : "NIE";
      return opt;
    };

    const isCorrect = !!a.correct_selected;

    return html`
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-600">
            Sekcja ${section?.nr}: ${section?.name}
          </div>

          ${this.isLastInSection
              ? html` <span class="text-xs text-gray-500"
              >Ostatnie pytanie w sekcji</span
              >`
              : html`
                <button class="btn btn-muted" @click=${() => this.interrupt()}>
                  Przerwij
                </button>
              `}
        </div>

        <!-- Pełne pytanie z media i zaznaczoną odpowiedzią -->
        <div class="rounded-xl border border-gray-200 bg-white p-4">
          ${renderQuestion({
            q,
            selected: a.selected,
            onSelect: () => {},
            readOnly: true,
          })}
        </div>

        <!-- Podsumowanie odpowiedzi -->
        <div
            class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-2"
        >
          <div class="text-xs uppercase tracking-wide text-gray-500">
            Podsumowanie
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <span class="text-gray-600">Twoja odpowiedź:</span>
            <span
                class="inline-flex h-7 items-center rounded-full bg-white px-3 text-xs font-semibold text-gray-800 border border-gray-200"
            >
              ${label(a.selected)}
            </span>

            ${isCorrect
                ? html` <span class="text-emerald-700">Prawidłowa ✓</span> `
                : html`
                  <span class="text-red-700">
                    Nieprawidłowa ✗ (poprawna: ${label(a.correct_answer)})
                  </span>
                `}
          </div>

          ${a.explanation
              ? html`
                <div class="pt-1 text-gray-700">
                  <span class="font-medium">Wyjaśnienie:</span>
                  <span class="block mt-1 text-gray-700">
                    ${a.explanation}
                  </span>
                </div>
              `
              : null}
        </div>

        <div class="flex justify-end">
          ${this.isLastInSection
              ? html`
                <button
                    class="btn btn-primary"
                    @click=${() => this.finishSection()}
                >
                  Zakończ sekcję
                </button>
              `
              : html`
                <button
                    class="btn btn-primary"
                    @click=${() => this.continueAfterFeedback()}
                >
                  Dalej
                </button>
              `}
        </div>
      </div>
    `;
  }
}

customElements.define("learn-app", LearnApp);
